import React, { useState } from 'react';
import { useGameStore } from '../../store/gameStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { AlertCircle, FileText, RotateCcw } from 'lucide-react';
import { gameAPI } from '../../services/gameAPI';
import { CaseSummaryModal } from '../game/CaseSummaryModal';
import { TutorialSummaryModal } from '../game/TutorialSummaryModal';
import { toast } from '../../hooks/use-toast';
import {
  formatSignedValue,
  formatPenalty,
  getSignedColorClass,
  getEvidenceRelevanceRow,
  getEvidenceVerdict,
} from '../../utils/scoreFormatting';
import { approachFitFromPercent } from '../../utils/effectiveness';
import { isFeaturedSessionId } from '../../utils/featuredSession';
import { restartFeaturedCase } from '../../utils/restartFeaturedCase';

export const AccusationModal = ({ sessionId, isOpen, onClose, onSuspectSelect, onSubmit, onViewSummary, onSummaryClose, onCaseClose }) => {
  const { suspects, discovered, endGame, selectedTheme, isTutorial, caseData } = useGameStore();

  // Only show discovered suspects in the accusation dropdown (with defensive checks)
  const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));

  // Tutorial dropdown click-leak prevention:
  // The tutorial is scripted to always end in a correct accusation against
  // the killer. Beta testers reported that wrong suspects in this dropdown
  // were still clickable (Radix renders options in a portal, so the tutorial
  // overlay's click-block does not reach them). Disable non-killer items at
  // the component level so wrong options become non-interactive regardless
  // of overlay rules.
  const tutorialKillerId = isTutorial ? caseData?.killer_id : null;
  const [selectedSuspect, setSelectedSuspect] = useState('');

  // Closed-trigger label: name only (tracker A4-DROPDOWN).
  // Radix mirrors the selected SelectItem's contents into the trigger, so the
  // closed trigger used to carry the portrait + occupation row too. That made
  // the trigger's geometry (width, text offset) vary with occupation length,
  // and beta testers used those differences to compare suspects — an oracle
  // the dropdown was never meant to expose. Passing children to SelectValue
  // suppresses the ItemText portal (Radix keeps the placeholder while the
  // value is empty), so the trigger renders this name and nothing else.
  const selectedSuspectName = discoveredSuspects.find(s => s.id === selectedSuspect)?.name;
  const [reasoning, setReasoning] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [actualKiller, setActualKiller] = useState(null);
  const [accusedSuspect, setAccusedSuspect] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryWasViewed, setSummaryWasViewed] = useState(false);
  const [restarting, setRestarting] = useState(false);
  // Structured retry state for failed retryable accusations (mirrors InterrogationModal pattern).
  // lastFailedAccusation: { suspectId } snapshot of the request that failed.
  const [accusationError, setAccusationError] = useState(null);
  const [lastFailedAccusation, setLastFailedAccusation] = useState(null);

  const themeAccentMap = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  };
  const themeAccent = themeAccentMap[selectedTheme] || themeAccentMap.noir;

  const handleSubmit = async (overrideSuspectId = null) => {
    // Retry path supplies the snapshot suspectId; otherwise use current selection.
    const suspectIdToUse = overrideSuspectId ?? selectedSuspect;
    if (!suspectIdToUse) return;

    try {
      setProcessing(true);

      let response;

      if (isTutorial) {
        // Tutorial mode: Simulate accusation response
        // Tutorial is scripted to always result in correct accusation

        // Guard against undefined suspects array
        if (!Array.isArray(suspects)) {
          console.error('Suspects array is undefined or invalid');
          toast({
            title: 'Error',
            description: 'Suspect data not loaded. Please try again.',
            variant: 'destructive'
          });
          return;
        }

        const accused = suspects.find(s => s.id === suspectIdToUse);

        // Guard against accused suspect not found
        if (!accused) {
          console.error('Selected suspect not found:', suspectIdToUse);
          toast({
            title: 'Error',
            description: 'Selected suspect could not be found. Please try again.',
            variant: 'destructive'
          });
          return;
        }

        // Fail closed: never fabricate a "correct" verdict the data doesn't support.
        // An inert dropdown lock has no observable symptom, so refuse visibly instead.
        if (!tutorialKillerId || accused.id !== tutorialKillerId) {
          console.warn(
            'AccusationModal: tutorial accusation refused (killer lock unresolved or wrong suspect).',
            { tutorialKillerId, accusedId: accused.id }
          );
          toast({
            title: 'Not quite',
            description: 'That is not who the tutorial is pointing to. Take another look at the evidence and try again.',
            variant: 'destructive'
          });
          return;
        }

        // Simple mock score for tutorial
        response = {
          correct: true,
          actualKiller: accused,
          accusedSuspect: accused,
          score: {
            score: 84,
            maxPossible: 100,
            grade: {
              name: 'Excellent',
              icon: '🎯',
              color: '#22c55e'
            },
            // Key names must match what `backend/scoring.py::calculate_score`
            // actually emits (pinned by `backend/tests/test_scoring_contract.py`),
            // or the renderer reads `undefined` and prints a flat 0. The tutorial
            // deliberately omits `documents`, `examine_skill` and
            // `evidence_relevance`: those terms are not part of its fixed 84, and
            // every row that reads them tolerates the section being ABSENT.
            breakdown: {
              base: 50,
              clues: { found: 4, total: 4, bonus: 20 },
              interviews: { conducted: 4, bonus: 10 },
              interrogation_skill: { average_effectiveness: 75, adjustment: 5 },
              consultations: { used: 0, total_penalty: 0 },
              cipher: { solved: true, unlocked: true, bonus: 10, penalty: 0, net: 10 },
              turns: { count: 25, bonus: -11 },
              accusation: { correct: true, bonus: 0, penalty: 0 }
            }
          }
        };
      } else {
        // Real game mode: Call backend API
        response = await gameAPI.accuse(sessionId, suspectIdToUse, reasoning);

        // Validate response shape before setting state
        if (!response || typeof response.correct !== 'boolean') {
          console.error('Invalid accusation response:', response);
          throw new Error('Invalid response from server');
        }
      }

      setIsCorrect(response.correct);
      setActualKiller(response.actualKiller || null);
      setAccusedSuspect(response.accusedSuspect || null);
      setScoreData(response.score || null);
      setShowResult(true);

      // Clear any prior retry banner now that the accusation succeeded.
      setAccusationError(null);
      setLastFailedAccusation(null);

      // Tutorial callback
      if (onSubmit) onSubmit(response.correct);

      if (response.correct) {
        endGame('won');
      } else {
        endGame('lost');
      }

    } catch (error) {
      console.error('Error processing accusation:', error);
      // Extract structured error detail from backend (matches interrogation contract:
      // { error, message, retryable: true } on a 503).
      const detail = error.response?.data?.detail;
      const detailMessage =
        typeof detail === 'string'
          ? detail
          : detail?.message || null;
      const isRetryable =
        error.isRetryable === true ||
        detail?.retryable === true ||
        error.response?.status === 503;

      if (isRetryable) {
        // Modal stays open; show inline banner with Try Again.
        setAccusationError(
          detailMessage ||
            error.message ||
            'The accusation couldn\'t be processed. Please try again.'
        );
        setLastFailedAccusation({ suspectId: suspectIdToUse });
      } else {
        toast({
          title: 'Failed to submit accusation',
          description: detailMessage || error.message || 'An error occurred while processing your accusation. Please try again.',
          variant: 'destructive'
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  // Retry the last failed retryable accusation using the snapshot captured at failure.
  const handleRetryAccusation = () => {
    if (!lastFailedAccusation) return;
    const { suspectId } = lastFailedAccusation;
    setAccusationError(null);
    // lastFailedAccusation is intentionally left in place — if retry also fails,
    // the error handler will overwrite it. Success path clears both.
    handleSubmit(suspectId);
  };

  // Only a featured case can be replayed: pro-gen has no restart endpoint.
  const isFeatured = isFeaturedSessionId(sessionId);

  const handlePlayAgain = async () => {
    // Double-click guard: the restart is a committing POST.
    if (restarting) return;
    setRestarting(true);
    const restarted = await restartFeaturedCase(sessionId);
    setRestarting(false);
    if (!restarted) return; // `restartFeaturedCase` already raised the toast.
    // Close both layers so the player lands on the fresh board, not on the
    // previous playthrough's verdict.
    setShowSummary(false);
    handleClose();
  };

  const handleClose = () => {
    setShowResult(false);
    setSelectedSuspect('');
    setReasoning('');
    setScoreData(null);
    setAccusationError(null);
    setLastFailedAccusation(null);
    onClose();
  };

  if (showResult) {
    // In tutorial mode, prevent closing result modal by clicking outside - must use buttons
    const handleResultOpenChange = (open) => {
      if (!open && isTutorial) return; // Prevent closing during tutorial
      if (!open) handleClose();
    };

    // Both are null when the section is absent (the tutorial's fabricated score)
    // or the accusation has not resolved. Never inferred from zeros.
    const evidenceRelevance = scoreData?.breakdown?.evidence_relevance;
    const relevanceRow = getEvidenceRelevanceRow(evidenceRelevance);
    const evidenceVerdict = getEvidenceVerdict({
      relevance: evidenceRelevance,
      isCorrect,
    });

    return (
      <Dialog open={isOpen} onOpenChange={handleResultOpenChange}>
        <DialogContent
          className="sm:max-w-2xl lg:max-w-3xl bg-slate-900 border-white/20 max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={isTutorial ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={isTutorial ? (e) => e.preventDefault() : undefined}
          hideCloseButton={isTutorial}
        >
          <div className="space-y-6 p-6">
            {isCorrect ? (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeAccent}20` }}>
                  <svg className="w-12 h-12" style={{ color: themeAccent }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-white">Case Closed!</h2>
                <p className="text-gray-300">
                  You've correctly identified {actualKiller?.name} as the killer.
                </p>
                <div className="bg-black/40 p-4 rounded-lg text-left">
                  <h3 className="font-semibold text-white mb-2">The Truth:</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {actualKiller?.motive || 'The investigation has been solved.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                </div>
                <h2 className="text-3xl font-bold text-white">Wrong Accusation</h2>
                <p className="text-gray-300">
                  {accusedSuspect?.name} wasn't the killer. The case is closed, unsolved.
                </p>
              </div>
            )}

            {/* Did what they found actually establish the accusation?
                An under-evidenced accusation is allowed and still reads as
                correct — this is where the screen says it scored as one. */}
            {evidenceVerdict && (
              <div
                className={`rounded-lg p-4 border text-left ${
                  evidenceVerdict.tone === 'affirm'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-amber-500/10 border-amber-500/30'
                }`}
                data-testid="accusation-evidence-verdict"
              >
                <p
                  className={`text-sm leading-relaxed ${
                    evidenceVerdict.tone === 'affirm' ? 'text-emerald-100' : 'text-amber-100'
                  }`}
                >
                  {evidenceVerdict.text}
                </p>
              </div>
            )}

            {/* Score Display */}
            {scoreData && (
              <div className="bg-black/60 rounded-lg p-5 border border-white/10">
                <div className="text-center mb-4">
                  <div className="text-4xl mb-1">{scoreData.grade?.icon}</div>
                  <h3 className="text-xl font-bold" style={{ color: scoreData.grade?.color }}>
                    {scoreData.grade?.name}
                  </h3>
                  <div className="text-3xl font-bold text-white mt-2">
                    {scoreData.score} / {scoreData.maxPossible}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {/* `SCORE_CONFIG["base_score"]` is 0, so this row is a
                      permanent "+0" in the real game and only adds noise to a
                      list whose whole job is to explain the total. Rendered
                      conditionally rather than deleted so it reappears on its own
                      if the constant is ever re-tuned above zero (the tutorial's
                      fabricated 50 still shows). */}
                  {scoreData.breakdown?.base > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Base Score</span>
                      <span className="text-white">+{scoreData.breakdown?.base}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-gray-400">
                    <span>Clues Found ({scoreData.breakdown?.clues?.found}/{scoreData.breakdown?.clues?.total})</span>
                    <span className="text-green-400">+{scoreData.breakdown?.clues?.bonus}</span>
                  </div>

                  {/* Gated on the section existing, not on its numbers: the
                      tutorial's fabricated score carries no `documents` term. */}
                  {scoreData.breakdown?.documents && (
                    <div className="flex justify-between text-gray-400">
                      <span>Documents Found ({scoreData.breakdown?.documents?.found}/{scoreData.breakdown?.documents?.total})</span>
                      <span className="text-green-400">+{scoreData.breakdown?.documents?.bonus}</span>
                    </div>
                  )}

                  {relevanceRow && (
                    <div className="flex justify-between text-gray-400" data-testid="accusation-relevance-row">
                      <span>
                        {relevanceRow.label}
                        {relevanceRow.note && (
                          <span className="block text-xs text-gray-500">{relevanceRow.note}</span>
                        )}
                      </span>
                      <span className={getSignedColorClass(relevanceRow.bonus)}>
                        {formatSignedValue(relevanceRow.bonus)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-gray-400">
                    <span>Interviews ({scoreData.breakdown?.interviews?.conducted})</span>
                    <span className="text-green-400">+{scoreData.breakdown?.interviews?.bonus}</span>
                  </div>

                  {scoreData.breakdown?.examine_skill?.examinations_count > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Examine Skill ({scoreData.breakdown?.examine_skill?.average_effectiveness}%)</span>
                      <span className={getSignedColorClass(scoreData.breakdown?.examine_skill?.total)}>
                        {formatSignedValue(scoreData.breakdown?.examine_skill?.total)}
                      </span>
                    </div>
                  )}

                  {scoreData.breakdown?.interrogation_skill?.average_effectiveness > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>
                        {/* The row is gated on `average_effectiveness > 0`, so
                            the value is always a number here. */}
                        Interrogation Skill ({
                          approachFitFromPercent(
                            scoreData.breakdown?.interrogation_skill?.average_effectiveness
                          )?.label
                        })
                      </span>
                      <span className={getSignedColorClass(scoreData.breakdown?.interrogation_skill?.adjustment)}>
                        {formatSignedValue(scoreData.breakdown?.interrogation_skill?.adjustment)}
                      </span>
                    </div>
                  )}

                  {scoreData.breakdown?.consultations?.used > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Consultations Used ({scoreData.breakdown?.consultations?.used})</span>
                      {/* `total_penalty` is already negative — do NOT negate it again. */}
                      <span className="text-red-400">
                        {formatSignedValue(scoreData.breakdown?.consultations?.total_penalty)}
                      </span>
                    </div>
                  )}

                  {/* Cipher Puzzle */}
                  {scoreData.breakdown?.cipher && !scoreData.breakdown?.cipher?.not_available && (
                    <div className="flex justify-between text-gray-400">
                      <span>
                        Cipher Puzzle {scoreData.breakdown?.cipher?.solved ? '(Solved)' : scoreData.breakdown?.cipher?.unlocked ? '(Unsolved)' : '(Not Found)'}
                      </span>
                      <span className={getSignedColorClass(scoreData.breakdown?.cipher?.net)}>
                        {formatSignedValue(scoreData.breakdown?.cipher?.net)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-gray-400">
                    <span>Turns ({scoreData.breakdown?.turns?.count})</span>
                    <span className={getSignedColorClass(scoreData.breakdown?.turns?.bonus)}>
                      {formatSignedValue(scoreData.breakdown?.turns?.bonus)}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-400 border-t border-white/10 pt-2 mt-2">
                    <span>Accusation</span>
                    <span className={isCorrect ? 'text-green-400' : 'text-red-400'}>
                      {isCorrect
                        ? `+${scoreData.breakdown?.accusation?.bonus ?? 0}`
                        : formatPenalty(scoreData.breakdown?.accusation?.penalty)}
                    </span>
                  </div>
                </div>

                {/* Tutorial Score Explanation */}
                {isTutorial && (
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-200 text-xs leading-relaxed">
                      <span className="font-semibold">Tutorial Max Score: 84/100</span>
                      <br />
                      The guided tutorial follows a fixed path, so you can't earn speed bonuses. 
                      In real cases, accusing the correct suspect earlier earns bonus points - solve in under 15 turns for +10 points, or under 20 turns for +5 points!
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  setShowSummary(true);
                  if (onViewSummary) onViewSummary();
                }}
                variant="outline"
                className={`flex-1 border-white/20 text-white hover:bg-white/10 ${isTutorial && !summaryWasViewed ? 'ring-4 ring-amber-400 animate-pulse' : ''}`}
                data-tutorial="view-summary"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Summary
              </Button>
              {isFeatured && (
                <Button
                  onClick={handlePlayAgain}
                  disabled={restarting}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  data-testid="accusation-play-again"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  <span aria-live="polite">{restarting ? 'Restarting...' : 'Play Again'}</span>
                </Button>
              )}
              <Button
                onClick={() => {
                  if (onCaseClose) onCaseClose();
                  handleClose();
                }}
                className={`flex-1 font-semibold ${isTutorial && summaryWasViewed ? 'ring-4 ring-amber-400 animate-pulse' : ''}`}
                style={{ backgroundColor: themeAccent }}
                data-tutorial="close-case"
              >
                Close Case
              </Button>
            </div>
          </div>
        </DialogContent>

        {/* Case Summary Modal - separate components for tutorial vs main game */}
        {isTutorial ? (
          <TutorialSummaryModal
            open={showSummary}
            onClose={() => {
              setShowSummary(false);
              setSummaryWasViewed(true);
              if (onSummaryClose) onSummaryClose();
            }}
            sessionId={sessionId}
          />
        ) : (
          <CaseSummaryModal
            open={showSummary}
            onClose={() => {
              setShowSummary(false);
              setSummaryWasViewed(true);
              if (onSummaryClose) onSummaryClose();
            }}
            sessionId={sessionId}
            // A restart from inside the summary has to drop the verdict layer
            // too, or the player lands back on the previous playthrough.
            onAfterRestart={() => {
              setShowSummary(false);
              handleClose();
            }}
          />
        )}
      </Dialog>
    );
  }

  return (
    // Tutorial mode: Radix `pointerdown`-outside bypasses TutorialOverlay's click blocker.
    // Lock the modal to the explicit suspect-select → submit flow.
    <Dialog open={isOpen} onOpenChange={isTutorial ? () => {} : handleClose}>
      <DialogContent
        className="sm:max-w-2xl lg:max-w-3xl bg-slate-900 border-white/20"
        onPointerDownOutside={isTutorial ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={isTutorial ? (e) => e.preventDefault() : undefined}
        hideCloseButton={isTutorial}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">Make Your Accusation</DialogTitle>
          <DialogDescription className="text-gray-400">
            Choose carefully. You only get one chance to accuse the killer.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0: direct grid child of DialogContent. Without it the track's
            min-content width follows the trigger's longest label. */}
        <div className="space-y-6 min-w-0">
          <div className="space-y-2">
            <label className="text-sm font-medium text-white" htmlFor="accusation-suspect-select">Who is the killer?</label>
            <Select value={selectedSuspect} onValueChange={(value) => {
              setSelectedSuspect(value);
              setAccusationError(null);
              setLastFailedAccusation(null);
              if (onSuspectSelect) onSuspectSelect(value);
            }}>
              <SelectTrigger id="accusation-suspect-select" className="bg-black/40 border-white/20 text-white" data-tutorial="suspect-select">
                <SelectValue placeholder="Select a suspect">
                  <span className="block truncate">{selectedSuspectName}</span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                {discoveredSuspects.map((suspect) => {
                  const isTutorialLockedOut = !!tutorialKillerId && suspect.id !== tutorialKillerId;
                  const isTutorialCorrect = !!tutorialKillerId && suspect.id === tutorialKillerId;
                  return (
                    <SelectItem
                      key={suspect.id}
                      value={suspect.id}
                      className="text-white focus:bg-white/10 focus:text-white"
                      data-tutorial-suspect={suspect.id}
                      data-tutorial-correct={isTutorialCorrect ? 'true' : undefined}
                      disabled={isTutorialLockedOut}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={suspect.portrait}
                          alt={suspect.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-medium">{suspect.name}</div>
                          <div className="text-xs text-gray-400">{suspect.occupation}</div>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-white">Your Reasoning (Optional)</label>
            <Textarea
              placeholder="Explain why you believe this person is the killer..."
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              className="bg-black/40 border-white/20 text-white min-h-[120px]"
            />
          </div>

          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-200">
                <p className="font-semibold mb-1">Warning</p>
                {/* SF-16: "final accusation" read as though earlier attempts
                    had been spent. There is only ever ONE — both backends
                    refuse a second (`routes/game.py` `accusation_already_resolved`,
                    `featured_cases/engine.py` `game_complete`) — so the live
                    copy says so outright.
                    The TUTORIAL mounts this same modal (`TutorialActionBar.jsx`)
                    and is out of scope, so it keeps the pre-SF-16 sentence
                    byte-for-byte; `AccusationModal.copy.test.jsx` pins both. */}
                <p data-testid="accusation-warning">
                  {isTutorial
                    ? "This is your final accusation. If you're wrong, the case will be closed and the real killer revealed."
                    : "You get one accusation — there is no second attempt. If you're wrong, the case closes unsolved."}
                </p>
              </div>
            </div>
          </div>

          {/* Retryable error banner (e.g. backend 503 with retryable: true).
              Mirrors the InterrogationModal retry UX — inline message + Try Again. */}
          {accusationError && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-red-900/30 border border-red-500/40 rounded-md px-3 py-2">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <AlertCircle className="w-4 h-4 mt-0.5 text-red-300 flex-shrink-0" />
                <p className="text-sm text-red-100 break-words">{accusationError}</p>
              </div>
              {lastFailedAccusation && (
                <Button
                  onClick={handleRetryAccusation}
                  disabled={processing}
                  size="sm"
                  variant="outline"
                  className="border-red-400/40 bg-red-900/40 text-red-100 hover:bg-red-800/60 hover:text-white"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Try Again
                </Button>
              )}
            </div>
          )}

          <div className="flex gap-3">
            {!isTutorial && (
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={() => handleSubmit()}
              disabled={!selectedSuspect || processing}
              className="flex-1 font-semibold"
              style={{ backgroundColor: selectedSuspect && !processing ? themeAccent : '#666' }}
              data-tutorial="submit-accusation"
            >
              {processing ? 'Processing...' : 'Make Accusation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
